import api from "./axios";

/**
 * Consumer API service
 * Handles all consumer-related operations
 */
export const consumersApi = {
  // Get a consumer by ID
  getConsumer: async (id) => {
    try {
      const response = await api.get(`/consumer/${id}`);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },
  // Get credit bundles for a client
  getCreditBundlesForClient: async (clientId) => {
    try {
      const response = await api.get(`/consumer/${clientId}/credit-bundles`);
      return response.data;
    } catch (error) {
      return { error: error?.message || "Failed to fetch credit bundles" };
    }
  },
  // Get consumer by client
  getConsumersByClient: async (clientId) => {
    try {
      const response = await api.get(`/consumer/client/${clientId}`);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  // Get consumer by email and client
  getConsumerByEmail: async (email, clientId) => {
    try {
      const response = await api.get(
        `/consumer/email/${encodeURIComponent(email)}/client/${clientId}`
      );
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  // Delete a consumer
  deleteConsumer: async (id) => {
    try {
      const response = await api.delete(`/consumer/${id}`);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  // Create payment order for Inveroak
  createPaymentOrder: async (payload) => {
    try {
      const response = await api.post(`/inveroakpayment/create-order`, payload);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },
  // Add credits to a consumer
  updateCredit: async (id, data) => {
    try {
      const response = await api.post(
        `/consumer/${id}/admin/CreditsUpdate`,
        data
      );
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  // Get consumer credit balance
  getCreditBalance: async (id) => {
    try {
      const response = await api.get(`/consumer/${id}/credits/balance`);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  // Get consumer credit ledger
  getCreditLedger: async (id, count = 20) => {
    try {
      const response = await api.get(
        `/consumer/${id}/credits/ledger?count=${count}`
      );
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  // Validate consumer credits
  validateCredits: async (id, requiredCredits = 1) => {
    try {
      const response = await api.get(
        `/consumer/${id}/validate-credits?requiredCredits=${requiredCredits}`
      );
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  getConversationByConsumerID: async (id) => {
    try {
      const response = await api.get(`/consumer/CustomerConversation/${id}`);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },
  getConversationWithCreditsByConsumerID: async (id) => {
    try {
      const response = await api.get(
        `/consumer/CustomerConversationWithCredits/${id}`
      );
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  getConsumerNotesByConsumerID: async (id) => {
    try {
      const response = await api.get(`/consumer/${id}/notes`);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },
  // POST a new note for a consumer
  addNoteToConsumer: async (id, note) => {
    try {
      const response = await api.post(`/consumer/${id}/notes/test`, note);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  // PUT to update a note
  updateConsumerNote: async (id, noteId, updatedNote) => {
    try {
      console.log("API updateConsumerNote called - id:", id, "noteId:", noteId);
      console.log("API updateConsumerNote payload:", updatedNote);
      const response = await api.put(
        `/consumer/${id}/notes/${noteId}`,
        updatedNote
      );
      console.log("API updateConsumerNote response:", response.data);
      return response.data;
    } catch (error) {
      console.error("API updateConsumerNote error:", error);
      console.error("Error response:", error.response);
      console.error("Error status:", error.response?.status);
      console.error("Error data:", error.response?.data);
      console.error("Error message:", error.message);
      return { error: getConsumerErrorMessage(error) };
    }
  },

  // DELETE a note
  deleteConsumerNote: async (id, noteId) => {
    try {
      const response = await api.delete(`/consumer/${id}/notes/${noteId}`);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  getConsumerCustomFields: async (consumerId) => {
    try {
      const response = await api.get(`/consumer/${consumerId}/custom-fields`);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  getConsumerCustomFieldById: async (fieldId) => {
    try {
      const response = await api.get(`/consumer/custom-fields/${fieldId}`);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  createConsumerCustomField: async (consumerId, fieldData) => {
    try {
      const response = await api.post(
        `/consumer/${consumerId}/custom-fields`,
        fieldData
      );
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  updateConsumerCustomField: async (fieldId, fieldData) => {
    try {
      const response = await api.put(
        `/consumer/custom-fields/${fieldId}`,
        fieldData
      );
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  updateConsumer: async (fieldId, fieldData) => {
    try {
      const response = await api.put(
        `/consumer/updateConsumer/${fieldId}`,
        fieldData
      );
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  deleteConsumerCustomField: async (fieldId) => {
    try {
      const response = await api.delete(`/consumer/custom-fields/${fieldId}`);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  //Search consumers with payload
  searchConsumers: async (payload) => {
    try {
      const response = await api.post(`/consumer/searchConsumer`, payload);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  // Get saved cards for a consumer
  getSavedCards: async (consumerId) => {
    try {
      const response = await api.get(`/inveroakpayment/saved-cards/${consumerId}`);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },

  // Use previous card for payment
  usePreviousCard: async (payload) => {
    try {
      const response = await api.post(`/inveroakpayment/use-previous-card`, payload);
      return response.data;
    } catch (error) {
      return { error: getConsumerErrorMessage(error) };
    }
  },
  getConsumerAcknowledgement: async (clientId) => {
  try {
    const response = await api.get(`/consumer/${clientId}/acknowledgement`);
    console.log("✅ Fetched consumer acknowledgement:", response.data);
    return response.data; // { data: ack | null }
  } catch (error) {
    throw error; // ⬅️ IMPORTANT: let axios throw
  }
},

  /**
   * Get acknowledgement status for a conversation (should banner be shown?)
   */
  getAcknowledgementStatus: async (conversationId) => {
    try {
      const response = await api.get(`/consumer/conversation/${conversationId}/ack-status`);
      console.log("✅ Fetched ack status:", response.data);
      return response.data; // { shouldShow: boolean, conversationId: number }
    } catch (error) {
      console.error("Error fetching ack status:", error);
      return { shouldShow: false }; // Default to not showing on error
    }
  },
};

/**
 * Helper function to extract error message from API response
 */
export const getConsumerErrorMessage = (error) => {
  if (error && error.response && error.response.data) {
    if (error.response.data.message) {
      return error.response.data.message;
    }
    if (
      error.response.data.errors &&
      Array.isArray(error.response.data.errors)
    ) {
      return error.response.data.errors[0];
    }
    if (
      error.response.data.errors &&
      typeof error.response.data.errors === "object"
    ) {
      const firstError = Object.values(error.response.data.errors)[0];
      if (Array.isArray(firstError)) {
        return firstError[0];
      }
      return String(firstError);
    }
  }
  return error?.message || "An unexpected error occurred";
};
